import type { getSitePrices } from "@/lib/pricing.functions";
import type { BookableMemberPackage } from "@/lib/member-package";
import { BOOKABLE_LESSON_SLUGS } from "@/lib/marketing-pricing";

export type SitePrice = Awaited<ReturnType<typeof getSitePrices>>[number];
export type MemberPackage = BookableMemberPackage;

export type BookingMode =
  | { kind: "single"; sitePrice: SitePrice }
  | { kind: "new-package"; sitePrice: SitePrice }
  | { kind: "existing-package"; memberPackage: MemberPackage }
  | { kind: "specialty" };

export function isBookableSlug(
  slug: string,
  bookableSlugs: readonly string[] | ReadonlySet<string>,
) {
  if (bookableSlugs instanceof Set) return bookableSlugs.has(slug);
  return (bookableSlugs as readonly string[]).includes(slug);
}

/** Package / Site price ids for createBooking / adminCreateBooking. */
export function bookingModePurchaseIds(mode: BookingMode | null): {
  memberPackageId?: string;
  sitePriceId?: string;
} {
  if (!mode) return {};
  if (mode.kind === "existing-package") return { memberPackageId: mode.memberPackage.id };
  if (mode.kind === "new-package") return { sitePriceId: mode.sitePrice.id };
  return {};
}

/** Display label for the selected BookingMode offering. */
export function bookingModeOfferingLabel(
  mode: BookingMode | null,
  specialtyLessonName?: string | null,
): string | null {
  if (!mode) return null;
  if (mode.kind === "specialty") return specialtyLessonName ?? null;
  if (mode.kind === "existing-package") return mode.memberPackage.label;
  return mode.sitePrice.label;
}

export function resolveLessonTypeBySlug<T extends { slug: string }>(
  lessonTypes: readonly T[],
  slug: string | null | undefined,
): T | null {
  if (!slug) return null;
  return lessonTypes.find((l) => l.slug === slug) ?? null;
}

/** Split Lesson types and Site prices into BookingMode selection groups. */
export function partitionBookingOfferings<
  TLesson extends { slug: string },
  TPrice extends { category: string },
>(
  lessonTypes: readonly TLesson[],
  sitePrices: readonly TPrice[],
  bookableSlugs: ReadonlySet<string> = BOOKABLE_LESSON_SLUGS,
) {
  return {
    singlePrices: sitePrices.filter((p) => p.category === "lesson_single"),
    packagePrices: sitePrices.filter((p) => p.category === "lesson_package"),
    specialtyLessons: lessonTypes.filter((l) => !bookableSlugs.has(l.slug)),
  };
}

/** Next-session copy for an existing Member package BookingMode. */
export function existingPackageSessionInfo(mode: BookingMode | null) {
  if (mode?.kind !== "existing-package") return null;
  const { memberPackage } = mode;
  return {
    nextSession: memberPackage.sessions_used + 1,
    total: memberPackage.sessions_total,
    remainingAfter: memberPackage.sessions_remaining - 1,
  };
}
